import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp, KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation1() {
  const { lesson, course, accent } = findLesson("piano-foundation", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF1-Notes-White-Animated.mp4"
        caption={`Twins and triplets — the piano's signposts · the same shape repeats every octave`}
        hint={`≈ 24 sec · find any note by shape, not by counting`}
      />
      <KeyHighlights>
        <Highlight>Two kinds of notes — <strong>white</strong> and <strong>black</strong>. Seven white note names cycle through the alphabet: <strong>A B C D E F G</strong>.</Highlight>
        <Highlight>The black keys group themselves into <strong>twins</strong> (groups of two) and <strong>triplets</strong> (groups of three). Every white note is named relative to those groups — never by counting from the edge.</Highlight>
        <Highlight>Once you know where E is, <em>every</em> E is the same shape — only the register changes. The same is true for every other note.</Highlight>
        <Highlight>The piano moves in a "zigzag" way; it isn't linear like a guitar. What you see is what you get — find the note, play it.</Highlight>
        <Highlight>Even non-piano learners are recommended to learn the piano notes — they're how you'll later understand intervals, chords, train your voice, and compose.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The two kinds of notes" title="White and black — and that's it" accent={accent.hex}>
        <p>
          Approaching any new instrument, the first question is the same: <em>where are the
          notes?</em> On the piano, the answer is reassuringly small — there are only
          two kinds of notes to think about. The <strong>white notes</strong> and the
          <strong> black notes</strong>. By colour, by shape, by name.
        </p>
        <p>
          Music itself uses twelve notes. The piano gives you seven of those twelve in white
          and the remaining five in black. This lesson covers all seven whites; the next
          lesson covers the five blacks.
        </p>
      </Insight>

      <Insight eyebrow="The twins and triplets" title="The map you'll use forever" accent={accent.hex}>
        <p>
          The black keys aren't randomly placed. They group themselves into clusters of two
          (we call them <strong>twins</strong>) and clusters of three (<strong>triplets</strong>),
          alternating across the entire keyboard. Those groups are how you'll find every white
          note.
        </p>
        <p>
          Why are the black keys grouped at all? Because there are gaps between certain white
          notes — a continuous row of identical black keys would be impossible to navigate. The
          twins and triplets are the keyboard's signposts.
        </p>

        <KeyIdea>
          You will never name a white note by counting from the edge of the keyboard. You will
          always name it by its position relative to the nearest twin or triplet. Every single
          time.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The seven location rules" title="One rule per white note" accent={accent.hex}>
        <p>This lesson walks through all seven, in this order:</p>
        <Steps>
          <Step><strong>E</strong> — to the <em>right</em> of the twins.</Step>
          <Step><strong>B</strong> — to the <em>right</em> of the triplets.</Step>
          <Step><strong>C</strong> — to the <em>left</em> of the twins.</Step>
          <Step><strong>F</strong> — to the <em>left</em> of the triplets.</Step>
          <Step><strong>D</strong> — <em>in between</em> the twins.</Step>
          <Step><strong>G</strong> — <em>between</em> triplet 1 and triplet 2.</Step>
          <Step><strong>A</strong> — <em>between</em> triplet 2 and triplet 3.</Step>
        </Steps>

        <Tip>
          When you go after a note, name the rule out loud — "C, <em>left of the twins</em>" —
          before pressing it. The narration locks the rule into long-term memory faster than
          silent practice does.
        </Tip>
      </Insight>

      <Insight title="The map · seven whites, all anchored to twins or triplets" accent={accent.hex}>
        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "C" },
            { pitch: "D4", color: "#3b82f6", label: "D" },
            { pitch: "E4", color: "#f59e0b", label: "E" },
            { pitch: "F4", color: "#a855f7", label: "F" },
            { pitch: "G4", color: "#ec4899", label: "G" },
            { pitch: "A4", color: "#06b6d4", label: "A" },
            { pitch: "B4", color: "#ef4444", label: "B" },
          ]}
          height={200}
        />
        <p className="prose-nsm text-sm">
          One full octave coloured. <span className="text-emerald-300">C</span> and{" "}
          <span className="text-amber-300">E</span> bracket the twins.{" "}
          <span className="text-purple-300">F</span> and{" "}
          <span className="text-rose-300">B</span> bracket the triplets.{" "}
          <span className="text-blue-300">D</span> sits inside the twin gap;{" "}
          <span className="text-pink-300">G</span> and{" "}
          <span className="text-cyan-300">A</span> sit inside the triplet gaps.
        </p>
      </Insight>

      <Insight eyebrow="Why every E is the same E" title="One shape, repeating across the keyboard" accent={accent.hex}>
        <p>
          The most useful insight in this lesson, once you know where
          E is, you'll be spot-on with every other E. The shape of every E is identical to the
          shape of every other E — only the register changes.
        </p>

        <Pull>
          It's sort of like the same colour — one colour being maybe dark red while the
          other one being bright red. But we still call it red colour, right? It's just
          a darker shade.
        </Pull>

        <p>
          This holds for every note on the keyboard. Drill the location rule once, and you've
          drilled the location of <em>every</em> instance of that note up and down the
          instrument. That's the gift the piano gives you that no other instrument does.
        </p>
      </Insight>

      <Insight eyebrow="The bigger picture" title="Notes are handpicked frequencies" accent={accent.hex}>
        <p>
          One last idea before moving on: there's no "first note" or "last
          note" in music. Music wraps around like a colour spectrum — the alphabet of notes
          recycles forever. The reason there are exactly twelve specific pitches per cycle
          isn't accidental either: out of the infinity of frequencies in nature, a handful
          were calibrated by humans so the ear could distinguish one note clearly from the
          next. The human ear hears roughly 20 to 20,000 Hz — and somewhere in that range, the
          twelve notes were chosen.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-2"
        label="Lesson 2 · Notes on the Piano (Black)"
        why="Now that all seven white notes are anchored to twins and triplets, the next short lesson closes the loop with the five blacks — sharps and flats, two names for the same key."
      />
    </LessonShell>
  );
}
